import type { Server } from 'miragejs';
import { response } from 'mocks/helpers';
import {
  COMPLIANCE_DOCUMENT_DELETED_CODE,
  X_COMPLIANCE_DOCUMENT_ID_HEADER,
  X_COMPLIANCE_WARNINGS_HEADER,
} from 'features/compliance/types';
import type {
  ComplianceDetail,
  ComplianceDocGeneratorDownloadCommand,
  ComplianceDocumentListItemDto,
} from 'features/compliance/types';
import {
  availableCerts,
  complianceDocTemplateFields,
  complianceDocTemplates,
  complianceDocumentListItems,
  complianceDocuments,
  complianceGeneratorPreview,
  complianceGeneratorStyles,
  DOWNLOAD_DOCUMENT_ID,
  DOWNLOAD_TRIGGERS,
  FORBIDDEN_DELETE_DOC_ID,
  mockPdfBody,
  sampleDownloadWarnings,
} from 'mocks/data/complianceDocuments';

/**
 * MirageJS handlers for the Compliance Document Generator (CDG), implementing
 * every CDG endpoint against deterministic fixtures so the hooks/wizard can
 * build against mocks before the backend lands (contract-first).
 *
 * Registered in `mocks/api/index.ts` under `enabledEndpoints`. Routes are
 * relative to `DOMAIN` (the cookie-auth `api` instance) — see
 * `mocks/browser.ts` (`this.urlPrefix = DOMAIN`).
 *
 * Each endpoint has a happy fixture plus every error-code branch (AC of
 * DL-59571). Error branches are selected deterministically via the
 * `DOWNLOAD_TRIGGERS` / `FORBIDDEN_DELETE_DOC_ID` constants in the fixtures.
 */

const PAGE_SIZE = 20;

const detail = (type: string, field: string, value: string): ComplianceDetail => ({
  type,
  field,
  value,
});

/**
 * Builds a contract-accurate error envelope body:
 * `{ status: 'error', code, data: null, error: { details: [{type,field,value}] } }`.
 * Passed to `response()` via its raw-`body` escape hatch so each handler can emit
 * `status: 'error'` (the shared helper defaults to `'fail'`) and the full
 * `{type,field,value}` detail shape (the helper's typed `error` omits `field`),
 * while keeping `response()` as the direct return (local-rules/prefer-response-helper).
 */
const errorBody = (code: string, details: ComplianceDetail[]) => ({
  status: 'error',
  code,
  data: null,
  error: { details },
});

const parseBody = (raw: unknown): Partial<ComplianceDocGeneratorDownloadCommand> => {
  if (typeof raw !== 'string') {
    return (raw as Partial<ComplianceDocGeneratorDownloadCommand>) ?? {};
  }
  try {
    return JSON.parse(raw) as Partial<ComplianceDocGeneratorDownloadCommand>;
  } catch {
    return {};
  }
};

export const complianceDocumentEndpoints = (server: Server) => {
  // GET /api/ComplianceDocument (cursor-paginated + server-side filtering)
  server.get('/api/ComplianceDocument', (schema, request) => {
    const params = new URL(request.url).searchParams;
    let items: ComplianceDocumentListItemDto[] = [...complianceDocumentListItems];

    const q = params.get('q');
    if (q) {
      const needle = q.toLowerCase();
      items = items.filter(
        d =>
          d.fileName.toLowerCase().includes(needle) ||
          (d.styleName ?? '').toLowerCase().includes(needle) ||
          (d.styleUserDefinedId ?? '').toLowerCase().includes(needle)
      );
    }
    // `status` and `templateType` accept a comma-separated set (the table's
    // multi-select Status / Type filters), so split and match on membership.
    const csv = (value: string | null): string[] =>
      value
        ? value
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
        : [];
    const statuses = csv(params.get('status'));
    if (statuses.length) items = items.filter(d => statuses.includes(d.status));
    const templateTypes = csv(params.get('templateType'));
    if (templateTypes.length) items = items.filter(d => templateTypes.includes(d.templateType));
    const styleId = params.get('styleId');
    if (styleId) items = items.filter(d => String(d.styleId) === styleId);
    const createdBy = params.get('createdBy');
    if (createdBy) items = items.filter(d => String(d.createdBy) === createdBy);

    // `sort` is `field:direction` (e.g. `createdAt:desc`); direction defaults to asc.
    const sortParam = params.get('sort');
    if (sortParam) {
      const [field, dir] = sortParam.split(':');
      const factor = dir?.toLowerCase() === 'desc' ? -1 : 1;
      const accessors: Record<string, (row: ComplianceDocumentListItemDto) => string | number> = {
        fileName: row => row.fileName.toLowerCase(),
        status: row => row.status,
        templateType: row => row.templateType,
        certCount: row => row.certCount,
        createdAt: row => row.createdAt,
      };
      const accessor = accessors[field];
      if (accessor) {
        items = [...items].sort((a, b) => {
          const av = accessor(a);
          const bv = accessor(b);
          if (av < bv) return -factor;
          if (av > bv) return factor;
          return 0;
        });
      }
    }

    // `cursor` here is the numeric page offset written by THIS handler (see
    // `nextCursor` below) — NOT the real backend's opaque base64+HMAC cursor.
    // An unrecognised cursor falls back to page 0.
    const cursor = params.get('cursor');
    const start = cursor ? Number(cursor.split('-')[0]) || 0 : 0;
    const pageItems = items.slice(start, start + PAGE_SIZE);
    const hasMore = start + PAGE_SIZE < items.length;
    const nextCursor = hasMore ? String(start + PAGE_SIZE) : '';

    return response(request.url, pageItems, {
      pagination: { cursor: nextCursor, hasMore },
    });
  });

  // GET /api/ComplianceDocument/:id
  server.get('/api/ComplianceDocument/:id', (schema, request) => {
    const { id } = request.params;
    const doc = complianceDocuments.find(d => d.id === id);
    if (!doc) {
      return response(request.url, undefined, {
        statusCode: 404,
        body: errorBody('entity_not_found', [detail('entity_not_found', 'id', id)]),
      });
    }
    return response(request.url, doc);
  });

  // DELETE /api/ComplianceDocument/:id (soft-delete → 200 + archived entity)
  server.delete('/api/ComplianceDocument/:id', (schema, request) => {
    const { id } = request.params;
    if (id === FORBIDDEN_DELETE_DOC_ID) {
      return response(request.url, undefined, {
        statusCode: 403,
        body: errorBody('action_not_allowed', [detail('action_not_allowed', 'id', id)]),
      });
    }
    const doc = complianceDocuments.find(d => d.id === id);
    if (!doc) {
      return response(request.url, undefined, {
        statusCode: 404,
        body: errorBody('entity_not_found', [detail('entity_not_found', 'id', id)]),
      });
    }
    const archived = { ...doc, status: 'Archived' as const };
    return response(request.url, archived, { code: COMPLIANCE_DOCUMENT_DELETED_CODE });
  });

  // GET /api/ComplianceDocGenerator/AvailableCerts?styleId=&includeFacility=
  server.get('/api/ComplianceDocGenerator/AvailableCerts', (schema, request) => {
    const styleId = new URL(request.url).searchParams.get('styleId');
    if (!styleId) {
      return response(request.url, undefined, {
        statusCode: 400,
        body: errorBody('bad_input', [detail('required', 'styleId', '')]),
      });
    }
    return response(request.url, availableCerts);
  });

  // Reused lookups (document only)
  server.get('/GetAllComplianceDocTemplates', (schema, request) =>
    response(request.url, complianceDocTemplates)
  );
  server.get('/v1.0/ComplianceDocTemplateField', (schema, request) =>
    response(request.url, complianceDocTemplateFields)
  );
  server.get('/StylesForComplianceDocGenerator/:complianceId', (schema, request) =>
    response(request.url, complianceGeneratorStyles)
  );

  // POST /ComplianceDocGenerator/preview (structural mirror)
  server.post('/ComplianceDocGenerator/preview', (schema, request) =>
    response(request.url, complianceGeneratorPreview)
  );

  // POST /ComplianceDocGenerator/download → binary PDF + warning headers
  server.post('/ComplianceDocGenerator/download', (schema, request) => {
    const body = parseBody(request.requestBody);
    const certificateFileIds = Array.isArray(body.certificateFileIds)
      ? body.certificateFileIds
      : [];
    const styleId = body.styleId;

    // Appendix-count cap (DL-60055) — count FILES, not certs.
    if (certificateFileIds.length > 50) {
      return response(request.url, undefined, {
        statusCode: 400,
        body: errorBody('merge_too_many_appendices', [
          detail(
            'merge_too_many_appendices',
            'certificateFileIds',
            String(certificateFileIds.length)
          ),
        ]),
      });
    }
    if (styleId === DOWNLOAD_TRIGGERS.payloadTooLarge) {
      return response(request.url, undefined, {
        statusCode: 400,
        body: errorBody('merge_payload_too_large', [
          detail('merge_payload_too_large', 'certificateFileIds', '200MB'),
        ]),
      });
    }
    // Unrecoverable — main render fails OR 100% of appendices fail.
    if (styleId === DOWNLOAD_TRIGGERS.unrecoverable) {
      return response(request.url, undefined, {
        statusCode: 409,
        body: errorBody('merge_failed', [detail('merge_failed', 'document', 'main render failed')]),
      });
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/pdf',
      [X_COMPLIANCE_DOCUMENT_ID_HEADER]: DOWNLOAD_DOCUMENT_ID,
    };
    // Partial success — skip-and-warn warnings ride in the header (base64 JSON).
    if (styleId === DOWNLOAD_TRIGGERS.partialWarnings) {
      headers[X_COMPLIANCE_WARNINGS_HEADER] = btoa(JSON.stringify(sampleDownloadWarnings));
    }
    return response(request.url, undefined, {
      statusCode: 200,
      headers,
      body: mockPdfBody(),
    });
  });
};
